import { useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import { paletteVar } from "@platform/design-system";

import {
  useResolvedRateSchedules,
  useUpdateRate,
  useUpdateProposalRate,
} from "../../../hooks/useRates";
import { formatRateValue } from "../../../utils/rateFormatters";
import { buildRateFieldPath } from "../../../utils/rateFieldPath";
import {
  getRenewalColumns,
  primarySectionSchedules,
  slotKeyOf,
} from "../../../utils/rateSets";

import { DebouncedNumericInput } from "./NumericInput";
import { RateReviewCell } from "./RateReviewCell";
import { RenewalHeaderCell, RenewalRateCell } from "./RenewalRateCell";
import {
  AG_BORDER,
  AG_HEADER_BG_COLOR,
  inputSx,
  gridRowSx,
  gridCellSx,
  headerCellSx,
  truncatedLabelCellSx,
  getUnitLabel,
  getQuantityLabel,
} from "./constants";

import type { AxiosInstance } from "axios";
import type { FC } from "react";
import type { BenefitPlan } from "../../../types/models";
import type { Rate, RateSchedule } from "../../../types";

/** Section header text styling (matches ag-grid data color / Montserrat) */
const SECTION_HEADER_COLOR = paletteVar("text-primary", "#00344F");
const SECTION_HEADER_FONT_SIZE = "14px";
const SECTION_HEADER_FONT_WEIGHT = 500;

/** Coverage tier codes that indicate dependent rates */
const DEPENDENT_TIER_CODES = [
  "employeeDependent",
  "spouseOnly",
  "childOnly",
] as const;

type DependentTierCode = (typeof DEPENDENT_TIER_CODES)[number];

/** Check if a rate has a dependent coverage tier code */
const isDependentRate = (rate: Rate): boolean =>
  DEPENDENT_TIER_CODES.includes(rate.coverageTierCode as DependentTierCode);

export interface DependentRateRendererProps {
  plan: BenefitPlan;
  axiosInstance?: AxiosInstance;
  /** Coverage type for LOC-specific behavior */
  coverageType?: string;
  onRateSaved: () => void;
  readOnly?: boolean;
}

/** Get the tier display label from a rate's coverageTierCode */
const getTierLabel = (rate: Rate): string => {
  switch (rate.coverageTierCode) {
    case "spouseOnly":
      return "Spouse";
    case "childOnly":
      return "Child";
    case "employeeDependent":
      return "Dependent";
    default:
      return "Dependent";
  }
};

/**
 * Derive whether rates are unit-based or volume-based.
 * usd → "unit"; everything else (volumePer*, per*OfSalary) → "volume".
 */
const deriveRatesBasedOn = (unit?: string): "unit" | "volume" => {
  if (!unit || unit === "usd") return "unit";
  return "volume";
};

/**
 * Derive whether dependent rates are combined or separate.
 * employeeDependent tier → combined; spouseOnly/childOnly → separate.
 */
const deriveDependentRateType = (rates: Rate[]): "combined" | "separate" =>
  rates.some((r) => r.coverageTierCode === "employeeDependent")
    ? "combined"
    : "separate";

// CSS Grid: Label + Value columns (matches FlatRateRenderer layout). A renewal column is
// appended when the incumbent has renewal rates, scrolling within the pinned cell.
const gridContainerSx = (showRenewal: boolean): Record<string, unknown> =>
  showRenewal
    ? {
        display: "grid",
        gridTemplateColumns: "minmax(90px, 1fr) repeat(2, minmax(80px, 1fr))",
        width: "100%",
        overflowX: "auto",
      }
    : { display: "grid", gridTemplateColumns: "3fr 2fr", width: "100%" };

/**
 * Renders dependent rate rows for a flat rate plan (primary schedule only).
 *
 * Structure:
 * - Section header: "Dependents / {unit|volume} / {combined|separate}"
 * - For each dependent tier: quantity row ({Tier} Unit/Volume) + rate row ({Tier} Rate)
 *
 * AD&D dependent rates are rendered by AdndRateRenderer in its own section.
 */
export const DependentRateRenderer: FC<DependentRateRendererProps> = ({
  plan,
  axiosInstance,
  coverageType,
  onRateSaved,
  readOnly,
}) => {
  const { data: rateSchedules, isLoading } = useResolvedRateSchedules(
    plan,
    axiosInstance,
  );

  // Find primary schedule (first non-AD&D schedule)
  const primarySchedule = useMemo(
    () =>
      rateSchedules?.find((s) => s.rateCoverageIndicator !== "adnd") ??
      rateSchedules?.[0],
    [rateSchedules],
  );

  // Filter dependent rates from primary schedule
  const dependentRates = useMemo(
    () => (primarySchedule?.rates || []).filter(isDependentRate),
    [primarySchedule?.rates],
  );

  const renewal = useMemo(
    () => getRenewalColumns(primarySectionSchedules(rateSchedules)),
    [rateSchedules],
  );
  const showRenewal = renewal.showRenewal;

  // Mutation hook for updating rates
  const updateRateMutation = useUpdateRate(axiosInstance!);
  const updateProposalRateMutation = useUpdateProposalRate(axiosInstance!);
  const rateMutation =
    plan.rateScheduleSource === "proposal"
      ? updateProposalRateMutation
      : updateRateMutation;

  /** Create a rate commit handler for a given schedule */
  const makeRateCommit =
    (schedule: RateSchedule | undefined) =>
    (rate: Rate) =>
    (value: string): void => {
      if (!axiosInstance || !schedule?.id || !rate.id) return;
      const numericValue = parseFloat(value);
      if (isNaN(numericValue) || numericValue === rate.rate) return;
      rateMutation.mutate(
        {
          scheduleId: schedule.id,
          rateId: rate.id,
          benefitPlanId: plan.id,
          rate: { rate: numericValue },
        },
        {
          onSuccess: () => onRateSaved(),
        },
      );
    };

  /**
   * Create a quantity commit handler for a given schedule. Unit-based ("usd")
   * rows store their count in numberOfLives (people, no currency) — the same
   * convention EE flat "usd" rates use — while volume-based rows use volume
   * (covered benefit/payroll). Keeping them in separate fields avoids
   * colliding with computeVolumeUnitRatePremium's usd+volume "bad data"
   * correction, which would otherwise misinterpret a legitimate unit-based
   * dependent count as a mis-stored volumePer1000 rate.
   */
  const makeQuantityCommit =
    (schedule: RateSchedule | undefined) =>
    (rate: Rate) =>
    (value: string): void => {
      if (!axiosInstance || !schedule?.id || !rate.id) return;
      const isUnitBased = rate.unit === "usd";
      const numericValue = isUnitBased
        ? parseInt(value, 10)
        : parseFloat(value);
      const currentValue = isUnitBased ? rate.numberOfLives : rate.volume;
      if (isNaN(numericValue) || numericValue === currentValue) return;
      rateMutation.mutate(
        {
          scheduleId: schedule.id,
          rateId: rate.id,
          benefitPlanId: plan.id,
          rate: isUnitBased
            ? { numberOfLives: numericValue }
            : { volume: numericValue },
        },
        {
          onSuccess: () => onRateSaved(),
        },
      );
    };

  const handlePrimaryRateCommit = makeRateCommit(primarySchedule);
  const handlePrimaryQuantityCommit = makeQuantityCommit(primarySchedule);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 1,
        }}
      >
        <CircularProgress size={16} />
      </Box>
    );
  }

  // Don't render anything if no primary dependent rates exist
  if (dependentRates.length === 0) {
    return null;
  }

  // Derive configuration from primary dependent rate data
  const ratesBasedOn = deriveRatesBasedOn(dependentRates[0]?.unit);
  const dependentRateType = deriveDependentRateType(dependentRates);
  const quantityLabel = getQuantityLabel(
    dependentRates[0]?.unit,
    false,
    coverageType,
  );
  const unitLabel = getUnitLabel(dependentRates[0]?.unit);

  return (
    <Box sx={{ width: "100%", mt: 0.5 }}>
      {/* Dependent Rates Section Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "32px",
          backgroundColor: AG_HEADER_BG_COLOR,
          borderBottom: AG_BORDER,
          borderTop: AG_BORDER,
          px: 1,
        }}
      >
        <Box
          sx={{
            fontWeight: SECTION_HEADER_FONT_WEIGHT,
            fontSize: SECTION_HEADER_FONT_SIZE,
            color: SECTION_HEADER_COLOR,
            fontStyle: "normal",
          }}
        >
          Dependents / {ratesBasedOn} / {dependentRateType}
        </Box>
      </Box>

      {/* Primary Dependent Rate Rows */}
      <Box sx={gridContainerSx(showRenewal)}>
        {/* Column header row — only needed to name Current vs Renewal */}
        {showRenewal && (
          <Box sx={gridRowSx}>
            <Box sx={{ ...headerCellSx }} />
            <Box sx={{ ...headerCellSx, justifyContent: "flex-end" }}>
              Current
            </Box>
            <RenewalHeaderCell show={showRenewal} label={renewal.columnLabel} />
          </Box>
        )}

        {dependentRates.map((rate, index) => {
          const tier = getTierLabel(rate);
          const isLastRow = index === dependentRates.length - 1;

          return (
            <Box key={rate.id} sx={{ display: "contents" }}>
              {/* Quantity row (Covered Benefit / Covered Payroll) */}
              <Box sx={gridRowSx}>
                <Box
                  title={`${tier} ${quantityLabel}`}
                  sx={truncatedLabelCellSx}
                >
                  {tier} {quantityLabel}
                </Box>
                <RateReviewCell
                  planId={plan.id}
                  fieldPath={
                    primarySchedule &&
                    buildRateFieldPath(
                      primarySchedule,
                      rate,
                      rate.unit === "usd" ? "numberOfLives" : "volume",
                    )
                  }
                  sx={{
                    ...gridCellSx,
                    borderRight: showRenewal ? AG_BORDER : "none",
                  }}
                >
                  <DebouncedNumericInput
                    serverValue={
                      rate.unit === "usd"
                        ? rate.numberOfLives != null
                          ? String(rate.numberOfLives)
                          : ""
                        : rate.volume != null
                          ? String(rate.volume)
                          : ""
                    }
                    onCommit={handlePrimaryQuantityCommit(rate)}
                    isCurrency={rate.unit !== "usd"}
                    sx={inputSx}
                    gridId={`dep-vol-${plan.id}`}
                    cellIndex={0}
                    columnsInRow={1}
                    readOnly={readOnly}
                    inputProps={{
                      "aria-label": `${tier} ${quantityLabel}`,
                    }}
                  />
                </RateReviewCell>
                {showRenewal && <Box sx={{ borderLeft: AG_BORDER }} />}
              </Box>

              {/* Rate row (with unit-aware label, e.g. "Spouse Rate per $1,000") */}
              <Box sx={gridRowSx}>
                <Box
                  title={`${tier} ${unitLabel}`}
                  sx={{
                    ...truncatedLabelCellSx,
                    ...(isLastRow && { borderBottom: "none" }),
                  }}
                >
                  {tier} {unitLabel}
                </Box>
                <RateReviewCell
                  planId={plan.id}
                  fieldPath={
                    primarySchedule &&
                    buildRateFieldPath(primarySchedule, rate, "rate")
                  }
                  sx={{
                    ...gridCellSx,
                    borderRight: showRenewal ? AG_BORDER : "none",
                    ...(isLastRow && { borderBottom: "none" }),
                  }}
                >
                  <DebouncedNumericInput
                    serverValue={formatRateValue(
                      rate.rate,
                      dependentRates[0]?.unit,
                      coverageType,
                    )}
                    onCommit={handlePrimaryRateCommit(rate)}
                    allowDecimals
                    isCurrency
                    sx={inputSx}
                    gridId={`dep-rate-${plan.id}`}
                    cellIndex={0}
                    columnsInRow={1}
                    readOnly={readOnly}
                    inputProps={{ "aria-label": `${tier} ${unitLabel}` }}
                  />
                </RateReviewCell>
                {showRenewal && (
                  <RenewalRateCell
                    slotKey={slotKeyOf(rate)}
                    renewal={renewal}
                    rowLabel={`${tier} ${unitLabel}`}
                    isLastRow={isLastRow}
                    coverageType={coverageType}
                  />
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
